import { Component, input, output, signal } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
/**
 * Component for a search input field that emits a value after a debounce period.
 * This component is typically used in headers or search bars where user input needs to be processed after a short delay.
 * It emits the current value after the user stops typing for a specified debounce time.
 */
@Component({
	selector: 'lvr-search',
	templateUrl: './search.html',
	styleUrl: './search.scss',
	imports: [MatInputModule, MatFormFieldModule, MatIconModule],
})
export class Search {
	/**
	 * The time in milliseconds to wait before emitting the changed value.
	 * Default is set to 300ms.
	 */
	public readonly debounceTime = input<number>(300);
	/**
	 * Output event that emits the changed value after the debounce time.
	 * This is triggered when the user stops typing for the specified debounce period.
	 */
	public readonly changed = output<string>();
	public readonly value = signal<string>('');
	private debounceTimer: ReturnType<typeof setTimeout> | null = null;
	public readonly ariaLabel = input<string>('Suche');

	/**
	 * Handles user input events from the search field.
	 * It updates the internal value and emits the changed event after a debounce period.
	 * @param event The input event triggered when the user types in the search field.
	 */
	public onUserInput(event: Event): void {
		const val = (event.target as HTMLInputElement).value;
		this.value.set(val);

		// Debounce: reset timer
		if (this.debounceTimer) clearTimeout(this.debounceTimer);

		this.debounceTimer = setTimeout(() => {
			this.changed.emit(val);
		}, this.debounceTime());
	}
}
